import { supabase } from '../config/supabase';
import { PatientService } from './PatientService'; // Manter, pois PatientService será migrado separadamente
import {
  AgendaEventIntegrated,
  EvolutionIntegrated,
  MonthlyClosingItem,
  MonthlyClosingSummary,
  AbsenceControl,
  AbsenceRecord,
  // AppointmentStatus, // Não é mais necessário, pois o status virá direto da tabela
} from '../types/integration';
import { UnifiedPatient } from '../types/unified-financial'; // Manter
import { BillingPeriodUtils } from '../utils/billingPeriod'; // Manter
import { getMonthlyPeriodBounds } from '../utils/dateHelpers';
import { getMedicalRecordAttachmentsObject, getMedicalRecordLifecycleStatus } from '../utils/medicalRecordStatus';
import { InvoiceService } from './InvoiceService';
import { isFinancialPreviewEnabled } from './FinancialReadService';
import {
  resolveAppointmentPaymentOriginForBilling,
  resolveAppointmentPaymentOriginForNoShow,
  resolveAppointmentPaymentOriginPatch,
} from '../utils/appointmentPaymentOrigin';

type RelinkOriginStatus = 'scheduled' | 'no_evaluated';

export interface RelinkEvolutionResult {
  previousAppointmentStatus: RelinkOriginStatus;
}

export class IntegrationService {
  // convertFirestoreDate não é mais necessária com datas ISO do Supabase

  static async isAppointmentLockedForPayment(
    appointmentId: string,
    clientId?: string
  ): Promise<boolean> {
    const { data: appointment, error: appointmentError } = await supabase
      .from('appointments')
      .select('professionalPaymentRecordId, clientId')
      .eq('appointmentId', appointmentId)
      .maybeSingle();

    if (appointmentError) throw appointmentError;
    if (!appointment?.professionalPaymentRecordId) return false;

    const resolvedClientId = clientId || appointment.clientId;
    if (!resolvedClientId) return false;

    const { data: payment, error: paymentError } = await supabase
      .from('professional_payment_records')
      .select('paymentIsPaid')
      .eq('paymentId', appointment.professionalPaymentRecordId)
      .eq('clientId', resolvedClientId)
      .maybeSingle();

    if (paymentError) throw paymentError;
    return Boolean(payment?.paymentIsPaid);
  }

  static async assertAppointmentEditable(
    appointmentId: string,
    clientId?: string
  ): Promise<void> {
    if (await this.isAppointmentLockedForPayment(appointmentId, clientId)) {
      throw new Error('Este agendamento já foi incluído em um repasse confirmado e não pode ser alterado.');
    }
  }

  private static async loadMedicalRecordAttachments(evolutionId: string): Promise<Record<string, any>> {
    const { data: existingRecord, error: fetchError } = await supabase
      .from('medical_records')
      .select('recordAttachments')
      .eq('recordId', evolutionId)
      .single();

    if (fetchError) {
      throw fetchError;
    }

    return getMedicalRecordAttachmentsObject(existingRecord?.recordAttachments);
  }

  static async linkEvolutionToAppointment(
    evolutionId: string,
    appointmentId: string
  ): Promise<void> {
    try {
      const currentAttachments = await this.loadMedicalRecordAttachments(evolutionId);
      const nowIso = new Date().toISOString();

      const { error } = await supabase
        .from("medical_records")
        .update({
          recordAttachments: {
            ...currentAttachments,
            agendamentoId: appointmentId,
            linkedAt: nowIso,
            unlinkedAt: null,
            appointmentUnlinkInfo: null,
          },
          recordUpdatedAt: nowIso,
        })
        .eq("recordId", evolutionId);

      if (error) {
        throw error;
      }
      console.log(`✅ Evolução ${evolutionId} vinculada ao agendamento ${appointmentId}`);
    } catch (error) {
      console.error('Erro ao vincular evolução ao agendamento:', error);
      throw error;
    }
  }

  static async findAppointmentForEvolution(
    patientName: string,
    evolutionDate: string,
    patientId?: string
  ): Promise<string | null> {
    try {
      const evolutionDay = new Date(evolutionDate).toISOString().split('T')[0];

      // Consulta inicial para agendamentos na data da evolução
      let queryBuilder = supabase
        .from("appointments")
        .select("appointmentId, appointmentStartDate, appointmentTitle, patientId, patients (patientFullName)") // Incluir patientFullName para comparação
        .eq("appointmentStartDate", evolutionDate) // Busca exata pela data de início
        .order("appointmentStartDate", { ascending: true });

      const { data: appointments, error } = await queryBuilder;

      if (error) {
        console.error("Erro ao buscar agendamentos no Supabase:", error);
        throw error;
      }

      for (const appointment of appointments) {
        const appointmentDay = new Date(appointment.appointmentStartDate).toISOString().split('T')[0];

        if (appointmentDay === evolutionDay) {
          // Buscar primeiro por patientId (mais preciso)
          if (patientId && appointment.patientId === patientId) {
            console.log('✅ Agendamento encontrado por patientId');
            return appointment.appointmentId;
          }

          // Fallback: buscar por nome (compatibilidade com eventos antigos e casos onde patientId não está direto)
          const fullPatientName = appointment.patients?.patientFullName || '';
          if (fullPatientName.toLowerCase().includes(patientName.toLowerCase())) {
            console.log('✅ Agendamento encontrado por nome (fallback)');
            return appointment.appointmentId;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Erro ao buscar agendamento para evolução:', error);
      return null;
    }
  }

  static async markAppointmentAsCompleted(
    appointmentId: string,
    evolutionId: string
  ): Promise<void> {
    try {
      await this.assertAppointmentEditable(appointmentId);
      const { data: appointment, error: fetchError } = await supabase
        .from("appointments")
        .select("appointmentPaymentOrigin")
        .eq("appointmentId", appointmentId)
        .single();

      if (fetchError) {
        throw fetchError;
      }

      const { error } = await supabase
        .from("appointments")
        .update({
          appointmentStatus: 'completed',
          appointmentEvolutionId: evolutionId,
          appointmentCompletedAt: new Date().toISOString(),
          appointmentPaymentOrigin: resolveAppointmentPaymentOriginPatch({
            currentOrigin: appointment?.appointmentPaymentOrigin,
            status: 'completed',
            hasNewFinancialImpact: false,
          }) ?? appointment?.appointmentPaymentOrigin ?? 'avulsa',
          appointmentUpdatedAt: new Date().toISOString() // Atualiza o timestamp de atualização
        })
        .eq("appointmentId", appointmentId);

      if (error) {
        throw error;
      }

      console.log(`✅ Agendamento ${appointmentId} marcado como concluído`);
    } catch (error) {
      console.error('Erro ao marcar agendamento como concluído:', error);
      throw error;
    }
  }

  static async registerAbsence(
    appointmentId: string,
    patientId: string,
    absenceDate: string,
    resolvedByUserId?: string,
    notes?: string,
    selectedInvoiceId?: string | null
  ): Promise<void> {
    try {
      await this.assertAppointmentEditable(appointmentId);
      const absenceMoment = new Date(absenceDate);
      if (Number.isNaN(absenceMoment.getTime())) {
        throw new Error("Data de falta inválida.");
      }

      const monthStart = new Date(absenceMoment.getFullYear(), absenceMoment.getMonth(), 1, 0, 0, 0, 0);
      const monthEnd = new Date(absenceMoment.getFullYear(), absenceMoment.getMonth() + 1, 0, 23, 59, 59, 999);

      const { data: appointmentData, error: appointmentError } = await supabase
        .from("appointments")
        .select("appointmentId, clientId, patientId, appointmentStatus, appointmentIsBilled, invoiceId, appointmentStartDate")
        .eq("appointmentId", appointmentId)
        .single();

      if (appointmentError || !appointmentData) {
        throw new Error(appointmentError?.message || "Agendamento não encontrado para registrar falta.");
      }

      const appointmentClientId = appointmentData.clientId;
      if (!appointmentClientId) {
        throw new Error("clientId do agendamento não encontrado.");
      }
      const effectivePatientId = appointmentData.patientId || patientId;

      if (
        appointmentData.appointmentStatus === 'no_show' &&
        appointmentData.appointmentIsBilled &&
        appointmentData.invoiceId
      ) {
        console.log(`✅ Falta do agendamento ${appointmentId} já estava registrada e faturada.`);
        return;
      }

      const monthReference = `${absenceMoment.getFullYear()}-${String(absenceMoment.getMonth() + 1).padStart(2, '0')}`;

      const { count: noShowCount, error: noShowCountError } = await supabase
        .from("appointments")
        .select("appointmentId", { count: "exact", head: true })
        .eq("clientId", appointmentClientId)
        .eq("patientId", effectivePatientId)
        .eq("appointmentStatus", "no_show")
        .neq("appointmentId", appointmentId)
        .gte("appointmentStartDate", monthStart.toISOString())
        .lte("appointmentStartDate", monthEnd.toISOString());

      if (noShowCountError) {
        throw noShowCountError;
      }

      const absenceNumber = (noShowCount || 0) + 1;

      const { data: financialProfileData, error: financialProfileError } = await supabase
        .from("financial_profiles")
        .select("financialProfileIndividualSessionPrice")
        .eq("patientId", effectivePatientId)
        .eq("clientId", appointmentClientId)
        .maybeSingle();

      if (financialProfileError) {
        throw financialProfileError;
      }

      const individualSessionPrice = Number(financialProfileData?.financialProfileIndividualSessionPrice || 150);
      const isChargeable = absenceNumber >= 3;
      const nowIso = new Date().toISOString();

      if (isChargeable) {
        const openInvoices = await InvoiceService.getOpenInvoicesForPatientMonth(
          effectivePatientId,
          appointmentClientId,
          monthReference
        );

        if (openInvoices.length > 1 && !selectedInvoiceId) {
          throw new Error('MÚLTIPLAS_FATURAS_ABERTAS');
        }
      }

      const absenceRecord: AbsenceRecord = {
        date: absenceMoment.toISOString(),
        absenceNumber,
        status: isChargeable ? "charged" : "unexcused",
        charge: isChargeable ? individualSessionPrice : 0,
        notes:
          notes ||
          (isChargeable
            ? `Falta #${absenceNumber} - Cobrança aplicada (a partir da 3ª falta)`
            : `Falta #${absenceNumber} - Sem cobrança (até 2 faltas gratuitas)`),
      };

      const appointmentUpdatePayload: Record<string, any> = {
        appointmentStatus: "no_show",
        appointmentAbsenceInfo: absenceRecord,
        appointmentIsBilled: false,
        invoiceId: null,
        appointmentBillingDate: null,
        appointmentUpdatedAt: nowIso,
        appointmentPaymentOrigin: resolveAppointmentPaymentOriginForNoShow(isChargeable),
      };

      if (resolvedByUserId) {
        appointmentUpdatePayload.appointmentResolvedAt = nowIso;
        appointmentUpdatePayload.appointmentResolvedBy = resolvedByUserId;
      }
      if (notes) {
        appointmentUpdatePayload.appointmentResolutionNotes = notes;
      }

      const { error: updateAppointmentError } = await supabase
        .from("appointments")
        .update(appointmentUpdatePayload)
        .eq("appointmentId", appointmentId);
      
      if (updateAppointmentError) {
        throw updateAppointmentError;
      }

      if (isChargeable) {
        const billing = await InvoiceService.createOrAppendNoShowCharge({
          patientId: effectivePatientId,
          clientId: appointmentClientId,
          appointmentId,
          appointmentDate: absenceMoment,
          absenceNumber,
          chargeAmount: individualSessionPrice,
          selectedInvoiceId,
          notes,
        });

        const { error: billedAppointmentError } = await supabase
          .from("appointments")
          .update({
            appointmentIsBilled: true,
            invoiceId: billing.invoiceId,
            appointmentBillingDate: nowIso,
            appointmentPaymentOrigin: resolveAppointmentPaymentOriginForBilling('avulsa'),
            appointmentUpdatedAt: nowIso,
          })
          .eq("appointmentId", appointmentId);

        if (billedAppointmentError) {
          throw billedAppointmentError;
        }
      }

      console.log(`✅ Falta registrada para paciente ${effectivePatientId} - Falta #${absenceNumber}`);

    } catch (error) {
      console.error('Erro ao registrar falta:', error);
      throw error;
    }
  }

  static async getEnrichedAppointments(
    startDate: string,
    endDate: string,
    clientId?: string
  ): Promise<AgendaEventIntegrated[]> {
    try {
      let appointmentsQuery = supabase
        .from("appointments")
        .select(`
          *,
          patients ( patientFullName ),
          users!appointments_userId_fkey ( userAssignedCalendarName )
        `)
        .gte("appointmentStartDate", startDate) // Filtra agendamentos a partir da data de início
        .lte("appointmentStartDate", endDate)   // Filtra agendamentos até a data de fim
        .order("appointmentStartDate", { ascending: true });

      if (clientId) {
        appointmentsQuery = appointmentsQuery.eq("clientId", clientId);
      }

      const { data: appointmentsData, error } = await appointmentsQuery;

      if (error) {
        console.error("Erro ao buscar agendamentos enriquecidos do Supabase:", error);
        throw error;
      }

      const events: AgendaEventIntegrated[] = [];

      for (const appointmentRow of appointmentsData) {
        const startDateTime = new Date(appointmentRow.appointmentStartDate);
        const endDateTime = appointmentRow.appointmentEndDate ? new Date(appointmentRow.appointmentEndDate) : null;

        const event: AgendaEventIntegrated = {
          id: appointmentRow.appointmentId,
          title: appointmentRow.appointmentTitle || "(Sem Título)",
          subject: appointmentRow.appointmentTitle, // Mapeando title para subject
          start: startDateTime,
          end: endDateTime || startDateTime, // Usar startDateTime se endDate for null
          calendar_name: appointmentRow.users?.userAssignedCalendarName || "(Sem Nome)",
          location: appointmentRow.appointmentLocation || undefined,
          patientId: appointmentRow.patientId || undefined,
          patientName: appointmentRow.patients?.patientFullName || undefined,
          status: appointmentRow.appointmentStatus || "scheduled",
          appointmentStatus: {
            hasEvolution: !!appointmentRow.appointmentEvolutionId,
            evolutionId: appointmentRow.appointmentEvolutionId || undefined,
            isBilled: appointmentRow.appointmentIsBilled || false,
            invoiceId: appointmentRow.invoiceId || undefined,
            paymentOrigin: appointmentRow.appointmentPaymentOrigin || undefined,
          },
          absenceInfo: appointmentRow.appointmentAbsenceInfo || undefined
        };

        events.push(event);
      }

      return events;
    } catch (error) {
      console.error("Erro ao buscar agendamentos enriquecidos:", error);
      throw error;
    }
  }

  static async getEnrichedEvolutions(
    startDate: string,
    endDate: string,
    clientId?: string
  ): Promise<EvolutionIntegrated[]> {
    try {
      let evolutionsQuery = supabase
        .from("medical_records") // Tabela medical_records no Supabase
        .select("*")
        .gte("recordDate", startDate) // Filtra evoluções a partir da data de início
        .lte("recordDate", endDate)   // Filtra evoluções até a data de fim
        .order("recordDate", { ascending: false });

      if (clientId) {
        evolutionsQuery = evolutionsQuery.eq("clientId", clientId);
      }

      const { data: evolutionsData, error } = await evolutionsQuery;

      if (error) {
        console.error("Erro ao buscar evoluções enriquecidas do Supabase:", error);
        throw error;
      }

      const evolutions: EvolutionIntegrated[] = [];

      for (const record of evolutionsData) {
        const evolutionDate = new Date(record.recordDate); // Usar recordDate
        const attachments =
          record.recordAttachments &&
          typeof record.recordAttachments === "object" &&
          !Array.isArray(record.recordAttachments)
            ? (record.recordAttachments as Record<string, any>)
            : {};

        const billedFromColumns =
          typeof record.recordIsBilled === "boolean" ? record.recordIsBilled : null;
        const billedFromAttachments = Boolean(attachments.isBilled);
        const normalizedIsBilled =
          billedFromColumns !== null ? billedFromColumns : billedFromAttachments;

        const normalizedInvoiceId =
          record.recordInvoiceId || attachments.invoiceId || undefined;
        const normalizedBillingDate =
          record.recordBillingDate || attachments.billingDate || undefined;
        
        const evolution: EvolutionIntegrated = {
          id: record.recordId,
          prontu_x00e1_riolookupid: record.patientId, // Mapeado para patientId
          data_x0020__x002f__x0020_hora: record.recordDate, // Usar recordDate
          // Adapte os campos abaixo conforme as colunas reais da sua tabela medical_records
          outras_x0020_interven_x00e7__x00: record.recordDescription, // Exemplo de mapeamento
          nome_paciente: "", // Este campo provavelmente virá de um JOIN com patients
          sobrenome_paciente: "", // Este campo provavelmente virá de um JOIN com patients
          numero_prontuario: "", // Este campo provavelmente virá de um JOIN com patients
          agendamentoId: attachments.agendamentoId || undefined,
          isBilled: normalizedIsBilled,
          billingDate: normalizedBillingDate,
          invoiceId: normalizedInvoiceId,
          status: getMedicalRecordLifecycleStatus(record.recordType, record.recordAttachments),
          created: record.recordCreatedAt // Usar recordCreatedAt
        };

        evolutions.push(evolution);
      }

      return evolutions;
    } catch (error) {
      console.error("Erro ao buscar evoluções enriquecidas:", error);
      throw error;
    }
  }

  static async generateMonthlyClosing(month: string, clientId?: string): Promise<MonthlyClosingSummary> {
    try {
      console.log(`📊 Gerando fechamento mensal para ${month}...`);
      if (!clientId) {
        throw new Error("ClientId é obrigatório para gerar fechamento mensal.");
      }

      const { startDate: expandedStart, endDate: expandedEnd } = BillingPeriodUtils.calculateExpandedPeriod(month);
      console.log(`📅 Período expandido de busca: ${BillingPeriodUtils.formatBillingPeriod(expandedStart, expandedEnd)}`);

      // Obter pacientes e seus perfis financeiros
      let patientsQuery = supabase
        .from('patients')
        .select(`
          patientId,
          patientFullName,
          patientCpf,
          patient_clients!inner(clientId),
          financial_profiles (
            financialProfileId,
            clientId,
            patientId,
            financialProfileIsActive,
            financialProfileHasPackage,
            financialProfileIndividualSessionPrice,
            financialProfileRenewalStatus,
            financialProfileObitoDate,
            financialProfileCreatedAt,
            financialProfileUpdatedAt
          ),
          invoices ( invoiceMonthReference, clientId )
        `);

      if (clientId) {
        patientsQuery = patientsQuery.eq('patient_clients.clientId', clientId);
      }

      const { data: patientsData, error: patientError } = await patientsQuery;

      if (patientError) {
        console.error('Erro ao buscar pacientes para fechamento mensal:', patientError);
        throw patientError;
      }
      const patients: UnifiedPatient[] = (patientsData || []).map((p: any) => {
        const financialProfiles = Array.isArray(p.financial_profiles)
          ? p.financial_profiles
          : p.financial_profiles
            ? [p.financial_profiles]
            : [];
        const scopedFinancialProfile = clientId
          ? financialProfiles.find((fp: any) => fp?.clientId === clientId)
          : financialProfiles[0];

        const invoicesRows = Array.isArray(p.invoices)
          ? p.invoices
          : p.invoices
            ? [p.invoices]
            : [];
        const scopedInvoices = clientId
          ? invoicesRows.filter((inv: any) => inv?.clientId === clientId)
          : invoicesRows;

        return {
          id: p.patientId,
          personalData: {
            name: p.patientFullName,
            cpf: p.patientCpf,
            // outros campos de patientData
          },
          financialProfile: {
            financialProfileId: scopedFinancialProfile?.financialProfileId,
            active: scopedFinancialProfile?.financialProfileIsActive,
            hasPackage: scopedFinancialProfile?.financialProfileHasPackage,
            individualSessionPrice: scopedFinancialProfile?.financialProfileIndividualSessionPrice,
            renewalStatus: scopedFinancialProfile?.financialProfileRenewalStatus,
            obitoDate: scopedFinancialProfile?.financialProfileObitoDate,
            // Outros campos do financialProfile
          },
          invoices: scopedInvoices.map((inv: any) => ({ month: inv.invoiceMonthReference })) // Mapear faturas
        } as UnifiedPatient;
      });

      const financialProfileIds = patients
        .map((patient) => patient.financialProfile?.financialProfileId)
        .filter((id): id is string => Boolean(id));

      const packagesByFinancialProfileId = new Map<string, any[]>();
      if (financialProfileIds.length > 0) {
        let packagesQuery = supabase
          .from("packages")
          .select(`
            packageId,
            clientId,
            financialProfileId,
            packageName,
            packagePrice,
            packageSessionsContracted,
            packageSessionsUsed,
            packageSessionsRemaining,
            packageValidityType,
            packageCustomValidityMonths,
            packagePaymentDate,
            packageRenewalDate,
            packageNotes,
            packageCreatedAt
          `)
          .in("financialProfileId", financialProfileIds)
          .order("packageCreatedAt", { ascending: false });

        if (clientId) {
          packagesQuery = packagesQuery.eq("clientId", clientId);
        }

        const { data: packagesData, error: packagesError } = await packagesQuery;
        if (packagesError) {
          console.error("Erro ao buscar pacotes para fechamento mensal:", packagesError);
          throw packagesError;
        }

        for (const pkg of packagesData || []) {
          const existingPackages = packagesByFinancialProfileId.get(pkg.financialProfileId) || [];
          existingPackages.push(pkg);
          packagesByFinancialProfileId.set(pkg.financialProfileId, existingPackages);
        }
      }

      for (const patient of patients) {
        const financialProfileId = patient.financialProfile?.financialProfileId;
        if (!financialProfileId) continue;

        const pkg = (packagesByFinancialProfileId.get(financialProfileId) || [])[0];
        if (!pkg) continue;

        patient.financialProfile.packageInfo = {
          packageId: pkg.packageId,
          clientId: pkg.clientId,
          financialProfileId: pkg.financialProfileId,
          packageName: pkg.packageName || undefined,
          packagePrice: Number(pkg.packagePrice || 0),
          packageSessionsContracted: Number(pkg.packageSessionsContracted || 0),
          packageSessionsUsed: Number(pkg.packageSessionsUsed || 0),
          packageSessionsRemaining: Number(pkg.packageSessionsRemaining || 0),
          packageValidityType: pkg.packageValidityType,
          packageCustomValidityMonths: pkg.packageCustomValidityMonths || undefined,
          packagePaymentDate: pkg.packagePaymentDate || "",
          packageRenewalDate: pkg.packageRenewalDate || undefined,
          packageNotes: pkg.packageNotes || undefined,
          packageCreatedAt: pkg.packageCreatedAt || new Date().toISOString(),
        };
        patient.financialProfile.hasPackage = true;
      }

      const appointments = await this.getEnrichedAppointments(
        expandedStart.toISOString(),
        expandedEnd.toISOString(),
        clientId
      );
      const evolutions = await this.getEnrichedEvolutions(
        expandedStart.toISOString(),
        expandedEnd.toISOString(),
        clientId
      );

      const { startDate: monthStart, endDate: monthEnd } = BillingPeriodUtils.calculateMonthlyPeriod(month);
      const monthlyBounds = getMonthlyPeriodBounds(month);
      const isCancelledStatus = (status?: string): boolean =>
        status === "cancelled" || status === "canceled";
      const isPaidInvoiceStatus = (status?: string): boolean => {
        const normalizedStatus = String(status || "").toLowerCase();
        return normalizedStatus === "paid" || normalizedStatus === "closed";
      };
      const { data: invoicesDueInPeriod, error: invoicesDueInPeriodError } = await supabase
        .from("invoices")
        .select("invoiceId, patientId, invoiceStatus, invoiceTotalAmount")
        .eq("clientId", clientId)
        .not("invoiceDueDate", "is", null)
        .gte("invoiceDueDate", monthlyBounds.startDateOnly)
        .lte("invoiceDueDate", monthlyBounds.endDateOnly);

      if (invoicesDueInPeriodError) {
        console.error("Erro ao buscar faturas por vencimento no período:", invoicesDueInPeriodError);
        throw invoicesDueInPeriodError;
      }

      let revenueByDueDate = (invoicesDueInPeriod || [])
        .filter((invoice) => !isCancelledStatus(String(invoice.invoiceStatus || "").toLowerCase()))
        .reduce((sum, invoice) => sum + Number(invoice.invoiceTotalAmount || 0), 0);
      const eligibleInvoicesForRevenue = (invoicesDueInPeriod || []).filter(
        (invoice) => !isCancelledStatus(String(invoice.invoiceStatus || "").toLowerCase())
      );
      const eligibleInvoiceIds = eligibleInvoicesForRevenue
        .map((invoice) => invoice.invoiceId)
        .filter((invoiceId): invoiceId is string => Boolean(invoiceId));
      const invoicePatientByInvoiceId = new Map<string, string>();
      const invoiceTotalsByPatient = new Map<
        string,
        {
          total: number;
          packageAmount: number;
          individualSessionsAmount: number;
          extraSessionsAmount: number;
          otherAmount: number;
          invoiceCount: number;
          hasPaidInvoice: boolean;
        }
      >();

      // A A3 grava faturas novas em financial_invoices. O fechamento mensal
      // ainda preserva a leitura legada, mas também precisa reconhecer essas
      // faturas para não mostrar receita zero após um atendimento concluído.
      let newFinancialInvoices: any[] = [];
      if (isFinancialPreviewEnabled(clientId)) {
        const { data: newFinancialInvoicePage, error: newFinancialInvoicesError } = await supabase.rpc("financial_a0_read", {
          p_client_id: clientId,
          p_section: "invoices",
          p_parent_id: null,
          p_search: "",
          p_offset: 0,
        });

        if (newFinancialInvoicesError) {
          const unavailableA0 = newFinancialInvoicesError.code === 'PGRST202'
            || newFinancialInvoicesError.code === '42883';
          if (!unavailableA0) {
            console.error("Erro ao buscar faturas financeiras novas para fechamento mensal:", newFinancialInvoicesError);
            throw newFinancialInvoicesError;
          }
          console.warn("A leitura financeira A0 não está instalada; o fechamento continuará somente com as faturas legadas.");
        } else {
          newFinancialInvoices = Array.isArray(newFinancialInvoicePage?.records)
            ? newFinancialInvoicePage.records.filter((invoice: any) => {
                const competence = String(invoice.competenceMonth || "").slice(0, 10);
                return competence >= monthlyBounds.startDateOnly && competence <= monthlyBounds.endDateOnly;
              })
            : [];
        }
      }
      const legacyInvoiceIds = new Set(eligibleInvoicesForRevenue.map(invoice => String(invoice.invoiceId || "")));

      for (const invoice of eligibleInvoicesForRevenue) {
        const invoiceId = String(invoice.invoiceId || "");
        const patientId = String(invoice.patientId || "");
        const total = Number(invoice.invoiceTotalAmount || 0);
        if (!invoiceId || !patientId) continue;

        invoicePatientByInvoiceId.set(invoiceId, patientId);

        const currentPatientTotals = invoiceTotalsByPatient.get(patientId) || {
          total: 0,
          packageAmount: 0,
          individualSessionsAmount: 0,
          extraSessionsAmount: 0,
          otherAmount: 0,
          invoiceCount: 0,
          hasPaidInvoice: false,
        };
        currentPatientTotals.total += total;
        currentPatientTotals.invoiceCount += 1;
        if (isPaidInvoiceStatus(invoice.invoiceStatus)) {
          currentPatientTotals.hasPaidInvoice = true;
        }
        invoiceTotalsByPatient.set(patientId, currentPatientTotals);
      }

      for (const invoice of newFinancialInvoices || []) {
        const invoiceId = String(invoice.financialInvoiceId || "");
        const patientId = String(invoice.patientId || "");
        if (!invoiceId || !patientId || (invoice.legacyInvoiceId && legacyInvoiceIds.has(String(invoice.legacyInvoiceId)))) continue;
        const total = Number(invoice.totalAmount || 0);
        revenueByDueDate += total;
        const currentPatientTotals = invoiceTotalsByPatient.get(patientId) || {
          total: 0,
          packageAmount: 0,
          individualSessionsAmount: 0,
          extraSessionsAmount: 0,
          otherAmount: 0,
          invoiceCount: 0,
          hasPaidInvoice: false,
        };
        currentPatientTotals.total += total;
        currentPatientTotals.invoiceCount += 1;
        if (isPaidInvoiceStatus(String(invoice.invoiceStatus || ""))) currentPatientTotals.hasPaidInvoice = true;
        invoiceTotalsByPatient.set(patientId, currentPatientTotals);
      }

      let packageRevenueFromInvoices = 0;
      let individualRevenueFromInvoices = 0;
      let extraSessionsRevenueFromInvoices = 0;
      let otherRevenueFromInvoices = 0;

      if (eligibleInvoiceIds.length > 0) {
        const { data: invoiceItemsData, error: invoiceItemsError } = await supabase
          .from("invoice_items")
          .select("invoiceId, invoiceItemType, invoiceItemAmount")
          .eq("clientId", clientId)
          .in("invoiceId", eligibleInvoiceIds);

        if (invoiceItemsError) {
          console.error("Erro ao buscar itens das faturas no período:", invoiceItemsError);
          throw invoiceItemsError;
        }

        for (const invoiceItem of invoiceItemsData || []) {
          const amount = Number(invoiceItem.invoiceItemAmount || 0);
          const invoiceId = String(invoiceItem.invoiceId || "");
          const patientId = invoicePatientByInvoiceId.get(invoiceId);
          if (!patientId) {
            continue;
          }

          const currentPatientTotals = invoiceTotalsByPatient.get(patientId) || {
            total: 0,
            packageAmount: 0,
            individualSessionsAmount: 0,
            extraSessionsAmount: 0,
            otherAmount: 0,
            invoiceCount: 0,
            hasPaidInvoice: false,
          };

          switch (invoiceItem.invoiceItemType) {
            case "package":
              packageRevenueFromInvoices += amount;
              currentPatientTotals.packageAmount += amount;
              break;
            case "session":
              individualRevenueFromInvoices += amount;
              currentPatientTotals.individualSessionsAmount += amount;
              break;
            case "additional":
              extraSessionsRevenueFromInvoices += amount;
              currentPatientTotals.extraSessionsAmount += amount;
              break;
            case "others":
            case "other":
            default:
              otherRevenueFromInvoices += amount;
              currentPatientTotals.otherAmount += amount;
              break;
          }

          invoiceTotalsByPatient.set(patientId, currentPatientTotals);
        }

        const groupedRevenueTotal =
          packageRevenueFromInvoices +
          individualRevenueFromInvoices +
          extraSessionsRevenueFromInvoices +
          otherRevenueFromInvoices;
        const remainder = Number((revenueByDueDate - groupedRevenueTotal).toFixed(2));
        if (Math.abs(remainder) >= 0.01) {
          otherRevenueFromInvoices += remainder;
        }
      }

      for (const [patientId, patientTotals] of invoiceTotalsByPatient.entries()) {
        const groupedPatientRevenue =
          patientTotals.packageAmount +
          patientTotals.individualSessionsAmount +
          patientTotals.extraSessionsAmount +
          patientTotals.otherAmount;
        const patientRemainder = Number((patientTotals.total - groupedPatientRevenue).toFixed(2));
        if (Math.abs(patientRemainder) >= 0.01) {
          patientTotals.otherAmount += patientRemainder;
        }
        invoiceTotalsByPatient.set(patientId, patientTotals);
      }
      const { data: paidProfessionalPayouts, error: paidProfessionalPayoutsError } = await supabase
        .from("professional_payment_records")
        .select("paymentTotalAmount")
        .eq("clientId", clientId)
        .eq("paymentIsPaid", true)
        .not("paymentPaidAt", "is", null)
        .gte("paymentPaidAt", monthlyBounds.startIso)
        .lt("paymentPaidAt", monthlyBounds.nextMonthStartIso);

      if (paidProfessionalPayoutsError) {
        console.error("Erro ao buscar repasses pagos no período:", paidProfessionalPayoutsError);
        throw paidProfessionalPayoutsError;
      }

      const totalProfessionalPayouts = (paidProfessionalPayouts || []).reduce(
        (sum, record) => sum + Number(record.paymentTotalAmount || 0),
        0
      );

      const items: MonthlyClosingItem[] = [];
      let packagesExpiring = 0;
      let excessiveAbsences = 0;
      let missingEvolutions = 0;

      for (const patient of patients) {
        const hasAppointmentInClosingPeriod = appointments.some((appointment) =>
          appointment.patientId === patient.id &&
          BillingPeriodUtils.isDateInPeriod(new Date(appointment.start), monthStart, monthEnd)
        );
        // Um atendimento concluído também torna o paciente elegível para o
        // fechamento. Isso mantém a tela coerente com a origem financeira nova
        // mesmo quando o perfil financeiro legado ainda não foi ativado.
        if (patient.financialProfile.active !== true && !hasAppointmentInClosingPeriod) continue;

        if (patient.financialProfile.hasPackage) {
          // Reavaliar validação de pacote com base nos novos dados do Supabase
        }

        const { startDate: patientStart, endDate: patientEnd } = BillingPeriodUtils.calculateBillingPeriod(patient, month);

        console.log(`
👤 ========== PROCESSANDO: ${patient.personalData.name} ==========`);
        console.log(`📅 Período de faturamento: ${BillingPeriodUtils.formatBillingPeriod(patientStart, patientEnd)}`);

        const allPatientAppointments = appointments.filter(apt => apt.patientId === patient.id);

        console.log(`📋 Total de agendamentos encontrados (todos os períodos): ${allPatientAppointments.length}`);

        const patientAppointments = allPatientAppointments.filter(apt => {
          const aptDate = new Date(apt.start);
          const isInPeriod = BillingPeriodUtils.isDateInPeriod(aptDate, monthStart, monthEnd);
          
          const dateStr = aptDate.toLocaleDateString('pt-BR');
          const statusIcon = isInPeriod ? '✅' : '❌';
          console.log(`   ${statusIcon} ${dateStr} - ${apt.status} - ${apt.title}`);
          
          return isInPeriod;
        });

        console.log(`📊 Agendamentos no período de faturamento: ${patientAppointments.length}`);

        const numberFromCandidates = (...candidates: any[]): number => {
          for (const candidate of candidates) {
            if (candidate === null || candidate === undefined || candidate === "") continue;
            const parsed = Number(candidate);
            if (Number.isFinite(parsed)) return parsed;
          }
          return 0;
        };

        const stringFromCandidates = (...candidates: any[]): string => {
          for (const candidate of candidates) {
            if (typeof candidate === "string" && candidate.trim().length > 0) {
              return candidate.trim();
            }
          }
          return "";
        };

        const patientEvolutions = evolutions.filter(evo =>
          evo.prontu_x00e1_riolookupid === patient.id && // Ajustar para o campo correto no Supabase
          evo.status !== 'invalidated' &&
          BillingPeriodUtils.isDateInPeriod(
            new Date(evo.data_x0020__x002f__x0020_hora),
            monthStart,
            monthEnd
          )
        );

        const completedSessions = patientAppointments.filter(apt => apt.status === 'completed');
        const noShowSessions = patientAppointments.filter(apt => apt.status === 'no_show');
        const cancelledSessions = patientAppointments.filter(apt => isCancelledStatus(apt.status));

        console.log(`   ✅ Completadas: ${completedSessions.length}`);
        console.log(`   ❌ Faltas (no-show): ${noShowSessions.length}`);
        console.log(`   🚫 Canceladas: ${cancelledSessions.length}`);

        let packageAmount = 0;
        let individualSessionsAmount = 0;
        let extraSessionsAmount = 0;
        let absenceCharges = 0;

        const hasPackage = patient.financialProfile.hasPackage;
        let packageInfo = undefined;

        if (hasPackage && patient.financialProfile.packageInfo) {
          const pkg: any = patient.financialProfile.packageInfo;
          const packageName = stringFromCandidates(pkg.packageName, pkg.name) || 'Pacote';
          const packagePrice = numberFromCandidates(pkg.packagePrice, pkg.price);
          const packageSessionsContracted = Math.max(
            0,
            Math.floor(numberFromCandidates(pkg.packageSessionsContracted, pkg.sessionsContracted))
          );
          const packageAdditionalSessionPrice = numberFromCandidates(
            pkg.packageAdditionalSessionPrice,
            pkg.additionalSessionPrice,
            patient.financialProfile.individualSessionPrice
          );
          const packagePaymentDateRaw = stringFromCandidates(pkg.packagePaymentDate, pkg.paymentDate);
          const packageRenewalDateRaw = stringFromCandidates(pkg.packageRenewalDate, pkg.renewalDate);
          const packageValidityType = stringFromCandidates(pkg.packageValidityType, pkg.validityType);
          const packageCustomValidityMonths = Math.max(
            1,
            Math.floor(numberFromCandidates(pkg.packageCustomValidityMonths, pkg.customValidityMonths, 1))
          );

          const paymentDate = packagePaymentDateRaw ? new Date(packagePaymentDateRaw) : new Date(patientStart);
          if (Number.isNaN(paymentDate.getTime())) {
            paymentDate.setTime(patientStart.getTime());
          }

          console.log(`
📦 ========== CÁLCULO DE PACOTE ==========`);
          console.log(`📦 Nome do pacote: ${packageName}`);
          console.log(`💰 Valor do pacote: R$ ${packagePrice.toFixed(2)}`);
          console.log(`📅 Data de pagamento: ${paymentDate.toLocaleDateString('pt-BR')}`);
          console.log(`🔢 Sessões contratadas: ${packageSessionsContracted}`);
          console.log(`💵 Preço sessão adicional: R$ ${packageAdditionalSessionPrice.toFixed(2)}`);

          const renewalDate = packageRenewalDateRaw ? new Date(packageRenewalDateRaw) : new Date(paymentDate);
          let monthsToAdd = 1;
          if (packageValidityType === '2_MONTHS') monthsToAdd = 2;
          else if (packageValidityType === '3_MONTHS') monthsToAdd = 3;
          else if (packageValidityType === 'CUSTOM') {
            monthsToAdd = packageCustomValidityMonths;
          }

          if (!packageRenewalDateRaw || Number.isNaN(renewalDate.getTime())) {
            renewalDate.setTime(paymentDate.getTime());
            renewalDate.setMonth(renewalDate.getMonth() + monthsToAdd);
            renewalDate.setDate(renewalDate.getDate() - 1);
            renewalDate.setHours(23, 59, 59, 999);
          }

          const now = new Date();
          const daysUntilRenewal = Math.ceil((renewalDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
          const isExpired = daysUntilRenewal < 0;

          console.log(`📅 Data de renovação: ${renewalDate.toLocaleDateString('pt-BR')}`);
          console.log(`⏰ Dias até renovação: ${daysUntilRenewal}`);
          console.log(`${isExpired ? '❌' : '✅'} Status: ${isExpired ? 'VENCIDO' : 'ATIVO'}`);

          const sessionsUsed = completedSessions.length;
          const extraSessions = Math.max(0, sessionsUsed - packageSessionsContracted);

          console.log(`
🧮 ========== CÁLCULO DE SESSÕES ==========`);
          console.log(`✅ Sessões completadas no período: ${sessionsUsed}`);
          console.log(`📦 Sessões contratadas no pacote: ${packageSessionsContracted}`);
          console.log(`➕ Sessões extras calculadas: ${extraSessions}`);
          console.log(`💰 Valor das sessões extras: R$ ${(extraSessions * packageAdditionalSessionPrice).toFixed(2)}`);

          if (sessionsUsed < 0) {
            console.error(`❌ ERRO: Número de sessões negativo!`);
          }
          if (extraSessions < 0) {
            console.error(`❌ ERRO: Número de sessões extras negativo!`);
          }

          if (completedSessions.length > 0) {
            console.log(`
📋 Lista de sessões completadas:`);
            completedSessions.forEach((session, index) => {
              const sessionDate = new Date(session.start).toLocaleDateString('pt-BR');
              console.log(`   ${index + 1}. ${sessionDate} - ${session.title}`);
            });
          }

          packageInfo = {
            name: packageName,
            price: packagePrice,
            sessionsContracted: packageSessionsContracted,
            sessionsUsed,
            sessionsRemaining: Math.max(0, packageSessionsContracted - sessionsUsed),
            expiryDate: renewalDate.toISOString(),
            isExpired,
            daysUntilExpiry: daysUntilRenewal
          };

          if (daysUntilRenewal <= 7 && daysUntilRenewal > 0) {
            packagesExpiring++;
          }
        } else {
          const sessionPrice = patient.financialProfile.individualSessionPrice || 150;
          individualSessionsAmount = completedSessions.length * sessionPrice;
        }

        const absenceSessionPrice = patient.financialProfile.individualSessionPrice || 150;
        const sortedNoShowSessions = [...noShowSessions].sort(
          (a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()
        );

        const absenceHistory: AbsenceRecord[] = sortedNoShowSessions.map((session, index) => {
          const absenceNumber = index + 1;
          const rawAbsenceInfo =
            session.absenceInfo && typeof session.absenceInfo === "object"
              ? (session.absenceInfo as Record<string, any>)
              : {};
          const explicitCharge = Number(rawAbsenceInfo.charge);
          const hasExplicitCharge = Number.isFinite(explicitCharge) && explicitCharge >= 0;

          const derivedCharge = absenceNumber >= 3 ? absenceSessionPrice : 0;
          const charge = hasExplicitCharge ? explicitCharge : derivedCharge;
          const status = charge > 0 ? "charged" : "unexcused";

          return {
            date: new Date(session.start).toISOString(),
            absenceNumber,
            status,
            charge,
            invoiceId:
              typeof rawAbsenceInfo.invoiceId === "string" && rawAbsenceInfo.invoiceId.trim().length > 0
                ? rawAbsenceInfo.invoiceId
                : undefined,
            invoiceDueDate:
              typeof rawAbsenceInfo.invoiceDueDate === "string" && rawAbsenceInfo.invoiceDueDate.trim().length > 0
                ? rawAbsenceInfo.invoiceDueDate
                : undefined,
            notes:
              typeof rawAbsenceInfo.notes === "string" && rawAbsenceInfo.notes.trim().length > 0
                ? rawAbsenceInfo.notes
                : undefined,
          };
        });

        const absenceControl: AbsenceControl = {
          totalAbsences: sortedNoShowSessions.length,
          unexcusedAbsences: sortedNoShowSessions.length,
          chargeableAbsences: absenceHistory.filter((absence) => absence.status === "charged").length,
          absenceHistory,
          lastResetDate: month,
        };

        if (absenceControl.unexcusedAbsences >= 3) {
          excessiveAbsences++;
        }

        const appointmentsWithoutEvolution = patientAppointments.filter(
          apt => apt.status === 'completed' && !apt.appointmentStatus?.evolutionId
        );
        if (appointmentsWithoutEvolution.length > 0) {
          missingEvolutions += appointmentsWithoutEvolution.length;
        }

        const alerts: string[] = [];
        if (packageInfo && packageInfo.daysUntilExpiry <= 3 && packageInfo.daysUntilExpiry > 0) {
          alerts.push(`⚠️ Pacote vence em ${packageInfo.daysUntilExpiry} dia(s)`);
        }
        if (packageInfo && packageInfo.isExpired) {
          alerts.push('❌ Pacote vencido - renovar');
        }
        if (absenceControl.unexcusedAbsences >= 3) {
          alerts.push(`⚠️ ${absenceControl.unexcusedAbsences} faltas no mês - conversar com paciente`);
        }
        if (appointmentsWithoutEvolution.length > 0) {
          alerts.push(`⚠️ ${appointmentsWithoutEvolution.length} sessão(ões) sem evolução registrada`);
        }

        const totalBilling = 0;

        const hasInvoice = patient.invoices?.some((inv) => inv.month === month) || false;
        
        if (hasInvoice) {
          console.log(`✅ [${patient.personalData.name}] Já possui fatura para ${month}`);
        }

        const item: MonthlyClosingItem = {
          patientId: patient.id,
          patientName: patient.personalData.name,
          patientNumber: patient.personalData.cpf || '',
          hasPackage,
          packageInfo,
          sessions: {
            scheduled: patientAppointments.length,
            completed: completedSessions.length,
            noShow: noShowSessions.length,
            cancelled: cancelledSessions.length
          },
          absences: absenceControl,
          billing: {
            packageAmount: 0,
            individualSessionsAmount: 0,
            extraSessionsAmount: 0,
            absenceCharges: 0,
            total: totalBilling,
            alreadyBilled: false
          },
          evolutions: patientEvolutions,
          appointments: patientAppointments,
          alerts
        };

        items.push(item);
      }

      for (const item of items) {
        const patientInvoiceTotals = invoiceTotalsByPatient.get(item.patientId);

        if (!patientInvoiceTotals) {
          item.billing.packageAmount = 0;
          item.billing.individualSessionsAmount = 0;
          item.billing.extraSessionsAmount = 0;
          item.billing.absenceCharges = 0;
          item.billing.total = 0;
          item.billing.alreadyBilled = false;
          item.billing.hasPaidInvoice = false;
          continue;
        }

        item.billing.packageAmount = Number(patientInvoiceTotals.packageAmount.toFixed(2));
        item.billing.individualSessionsAmount = Number(patientInvoiceTotals.individualSessionsAmount.toFixed(2));
        item.billing.extraSessionsAmount = Number(patientInvoiceTotals.extraSessionsAmount.toFixed(2));
        item.billing.absenceCharges = Number(patientInvoiceTotals.otherAmount.toFixed(2));
        item.billing.total = Number(patientInvoiceTotals.total.toFixed(2));
        item.billing.alreadyBilled = true;
        item.billing.hasPaidInvoice = patientInvoiceTotals.hasPaidInvoice;
      }

      const getPackageValidityMonths = (pkg: any): number => {
        switch (pkg?.packageValidityType) {
          case "2_MONTHS":
            return 2;
          case "3_MONTHS":
            return 3;
          case "CUSTOM":
            return Math.max(1, Number(pkg?.packageCustomValidityMonths || 1));
          default:
            return 1;
        }
      };

      const getPackageInterval = (pkg: any): { start: Date; end: Date } | null => {
        if (!pkg?.packagePaymentDate) return null;

        const start = new Date(pkg.packagePaymentDate);
        if (Number.isNaN(start.getTime())) return null;
        start.setHours(0, 0, 0, 0);

        const renewalDate = pkg.packageRenewalDate ? new Date(pkg.packageRenewalDate) : null;
        if (renewalDate && !Number.isNaN(renewalDate.getTime())) {
          renewalDate.setHours(23, 59, 59, 999);
          return { start, end: renewalDate };
        }

        const validityMonths = getPackageValidityMonths(pkg);
        const derivedEnd = new Date(start);
        derivedEnd.setMonth(derivedEnd.getMonth() + validityMonths);
        derivedEnd.setDate(derivedEnd.getDate() - 1);
        derivedEnd.setHours(23, 59, 59, 999);

        return { start, end: derivedEnd };
      };

      const isPackageActiveInPeriod = (pkg: any, periodStart: Date, periodEnd: Date): boolean => {
        const interval = getPackageInterval(pkg);
        if (!interval) return false;
        return interval.start <= periodEnd && interval.end >= periodStart;
      };

      const activePatientIds = new Set<string>();
      for (const patient of patients) {
        const hasNonCancelledAppointmentInMonth = appointments.some((appointment) => {
          if (appointment.patientId !== patient.id) return false;
          if (isCancelledStatus(appointment.status)) return false;
          return BillingPeriodUtils.isDateInPeriod(new Date(appointment.start), monthStart, monthEnd);
        });

        const financialProfileId = patient.financialProfile?.financialProfileId;
        const patientPackages = financialProfileId
          ? packagesByFinancialProfileId.get(financialProfileId) || []
          : [];
        const hasActivePackageInMonth = patientPackages.some((pkg) =>
          isPackageActiveInPeriod(pkg, monthStart, monthEnd)
        );

        if (hasNonCancelledAppointmentInMonth || hasActivePackageInMonth) {
          activePatientIds.add(patient.id);
        }
      }

      const activePatients = patients.filter((patient) => activePatientIds.has(patient.id));
      const patientsWithPackage = activePatients.filter((patient) => {
        const financialProfileId = patient.financialProfile?.financialProfileId;
        if (!financialProfileId) return false;
        const patientPackages = packagesByFinancialProfileId.get(financialProfileId) || [];
        return patientPackages.some((pkg) => isPackageActiveInPeriod(pkg, monthStart, monthEnd));
      });
      const appointmentsInClosingPeriod = appointments.filter((appointment) =>
        BillingPeriodUtils.isDateInPeriod(new Date(appointment.start), monthStart, monthEnd)
      );
      const sessionsRealized = appointmentsInClosingPeriod.filter(
        (appointment) =>
          appointment.status === 'completed'
          || appointment.status === 'no_billed'
          || appointment.status === 'no_evaluated'
      ).length;

      const summary: MonthlyClosingSummary = {
        month,
        period: {
          startDate: monthlyBounds.startDateOnly,
          endDate: monthlyBounds.endDateOnly
        },
        overview: {
          totalPatients: patients.length,
          activePatients: activePatients.length,
          patientsWithPackage: patientsWithPackage.length,
          patientsIndividual: activePatients.length - patientsWithPackage.length
        },
        appointments: {
          total: appointmentsInClosingPeriod.length,
          completed: sessionsRealized,
          noShow: appointmentsInClosingPeriod.filter(a => a.status === 'no_show').length,
          cancelled: appointmentsInClosingPeriod.filter(a => isCancelledStatus(a.status)).length,
          pendingEvolution: missingEvolutions
        },
        financial: {
          packageRevenue: packageRevenueFromInvoices,
          individualRevenue: individualRevenueFromInvoices,
          extraSessionsRevenue: extraSessionsRevenueFromInvoices,
          otherRevenue: otherRevenueFromInvoices,
          absenceCharges: otherRevenueFromInvoices,
          revenueByDueDate,
          totalProfessionalPayouts,
          totalRevenue: revenueByDueDate,
          alreadyBilled: invoiceTotalsByPatient.size,
          toBill: 0
        },
        alerts: {
          packagesExpiring,
          excessiveAbsences,
          missingEvolutions,
          overdueInvoices: 0
        },
        items
      };

      console.log(`✅ Fechamento gerado: ${items.length} pacientes processados`);
      return summary;

    } catch (error) {
      console.error('Erro ao gerar fechamento mensal:', error);
      throw error;
    }
  }

  static async markEvolutionAsBilled(
    evolutionId: string,
    invoiceId: string,
    clientId?: string
  ): Promise<void> {
    try {
      let fetchQuery = supabase
        .from("medical_records")
        .select("recordAttachments")
        .eq("recordId", evolutionId);

      if (clientId) {
        fetchQuery = fetchQuery.eq("clientId", clientId);
      }

      const { data: existingRecord, error: fetchError } = await fetchQuery.single();

      if (fetchError) {
        throw fetchError;
      }

      const currentAttachments =
        existingRecord?.recordAttachments &&
        typeof existingRecord.recordAttachments === "object" &&
        !Array.isArray(existingRecord.recordAttachments)
          ? (existingRecord.recordAttachments as Record<string, any>)
          : {};

      const nowIso = new Date().toISOString();
      let query = supabase
        .from("medical_records") // Tabela medical_records no Supabase
        .update({
          recordIsBilled: true,
          recordInvoiceId: invoiceId,
          recordBillingDate: nowIso,
          recordAttachments: {
            ...currentAttachments,
            isBilled: true,
            invoiceId,
            billingDate: nowIso
          },
          recordUpdatedAt: nowIso
        })
        .eq("recordId", evolutionId);

      if (clientId) {
        query = query.eq("clientId", clientId);
      }

      const { error } = await query;

      if (error) {
        throw error;
      }
      console.log(`✅ Evolução ${evolutionId} marcada como faturada`);
    } catch (error) {
      console.error('Erro ao marcar evolução como faturada:', error);
      throw error;
    }
  }

  static async markAppointmentAsBilled(
    appointmentId: string,
    invoiceId: string,
    clientId?: string
  ): Promise<void> {
    try {
      await this.assertAppointmentEditable(appointmentId, clientId);
      let query = supabase
        .from("appointments")
        .update({
          appointmentIsBilled: true,
          invoiceId: invoiceId,
          appointmentBillingDate: new Date().toISOString(),
          appointmentUpdatedAt: new Date().toISOString()
        })
        .eq("appointmentId", appointmentId);

      if (clientId) {
        query = query.eq("clientId", clientId);
      }

      const { error } = await query;

      if (error) {
        throw error;
      }

      console.log(`✅ Agendamento ${appointmentId} marcado como faturado`);
    } catch (error) {
      console.error('Erro ao marcar agendamento como faturado:', error);
      throw error;
    }
  }

  /**
   * 🆕 Listar agendamentos de um paciente específico
   * @param patientId ID do paciente
   * @param daysRange Número de dias para buscar (padrão: 180 dias antes e 30 depois para cobrir histórico extenso)
   * @returns Lista de agendamentos do paciente
   */
  static async getPatientAppointments(
    patientId: string,
    daysRange: number = 180
  ): Promise<AgendaEventIntegrated[]> {
    try {
      console.log(`🔍 Buscando agendamentos para paciente: ${patientId}`);

      const now = new Date();
      const rangeStart = new Date(now.getTime() - (daysRange * 24 * 60 * 60 * 1000));
      const rangeEnd = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000)); // Apenas 30 dias no futuro

      const { data: appointmentsData, error } = await supabase
        .from("appointments")
        .select(`
          *,
          patients ( patientFullName ),
          users!appointments_userId_fkey ( userAssignedCalendarName )
        `)
        .eq("patientId", patientId)
        .gte("appointmentStartDate", rangeStart.toISOString())
        .lte("appointmentStartDate", rangeEnd.toISOString())
        .order("appointmentStartDate", { ascending: false }); // Ordenar do mais recente para o mais antigo

      if (error) {
        console.error("Erro ao buscar agendamentos do paciente no Supabase:", error);
        throw error;
      }

      const appointments: AgendaEventIntegrated[] = [];

      for (const appointmentRow of appointmentsData) {
        const startDateTime = new Date(appointmentRow.appointmentStartDate);
        const endDateTime = appointmentRow.appointmentEndDate ? new Date(appointmentRow.appointmentEndDate) : startDateTime; // Usar startDateTime se endDate for null

        appointments.push({
          id: appointmentRow.appointmentId,
          title: appointmentRow.appointmentTitle || "(Sem Título)",
          subject: appointmentRow.appointmentTitle, 
          start: startDateTime,
          end: endDateTime,
          userId: appointmentRow.userId || undefined,
          doctorId: appointmentRow.doctorId || undefined,
          patientId: appointmentRow.patientId || undefined,
          patientName: appointmentRow.patients?.patientFullName || undefined,
          status: appointmentRow.appointmentStatus || "scheduled",
          location: appointmentRow.appointmentLocation || undefined,
          calendar_name: appointmentRow.users?.userAssignedCalendarName || "(Não Atribuído)",
          appointmentStatus: {
            hasEvolution: !!appointmentRow.appointmentEvolutionId,
            evolutionId: appointmentRow.appointmentEvolutionId || undefined,
            isBilled: appointmentRow.appointmentIsBilled || false,
            invoiceId: appointmentRow.invoiceId || undefined,
            paymentOrigin: appointmentRow.appointmentPaymentOrigin || undefined,
          },
          absenceInfo: appointmentRow.appointmentAbsenceInfo || undefined
        });
      }

      console.log(`✅ ${appointments.length} agendamentos encontrados para paciente ${patientId}`);
      return appointments;
    } catch (error) {
      console.error('❌ Erro ao buscar agendamentos do paciente:', error);
      console.error('Detalhes do erro:', error);
      return [];
    }
  }

  /**
   * 🆕 Obter agendamentos categorizados para dropdown de vínculo de evoluções
   * @param patientId ID do paciente
   * @param currentEvolutionId ID da evolução atual (para incluir o vínculo atual mesmo que seja antigo)
   * @returns Objeto com agendamentos categorizados
   */
  static async getCategorizedAppointmentsForLinking(
    patientId: string,
    currentEvolutionId?: string,
    preferredAppointmentId?: string
  ): Promise<{
    available: AgendaEventIntegrated[];
    linked: AgendaEventIntegrated[];
    currentLink: AgendaEventIntegrated | null;
  }> {
    try {
      console.log(`🔍 Buscando agendamentos categorizados para paciente: ${patientId}`);

      const allAppointments = await this.getPatientAppointments(patientId, 180);

      const available: AgendaEventIntegrated[] = [];
      const linked: AgendaEventIntegrated[] = [];
      let currentLink: AgendaEventIntegrated | null = null;

      allAppointments.forEach(appointment => {
        if (currentEvolutionId && appointment.appointmentStatus?.evolutionId === currentEvolutionId) {
          currentLink = appointment;
        }
        else if (appointment.status === 'scheduled' || appointment.status === 'no_evaluated') {
          available.push(appointment);
        }
        else if (appointment.status === 'completed' || appointment.status === 'no_billed') {
          linked.push(appointment);
        }
      });

      // Ordenar disponíveis por data (mais antigos primeiro)
      available.sort((a, b) => a.start.getTime() - b.start.getTime());

      // Ordenar sessões avaliadas por data (mais recentes primeiro)
      linked.sort((a, b) => b.start.getTime() - a.start.getTime());

      const limitedLinked = linked.slice(0, 12);

      const appointmentAlreadyIncluded = preferredAppointmentId
        ? Boolean(
            currentLink?.id === preferredAppointmentId ||
            available.some((appointment) => appointment.id === preferredAppointmentId) ||
            limitedLinked.some((appointment) => appointment.id === preferredAppointmentId),
          )
        : false;

      if (preferredAppointmentId && !appointmentAlreadyIncluded) {
        const { data: preferredAppointmentRow, error: preferredAppointmentError } = await supabase
          .from("appointments")
          .select(`
            *,
            patients ( patientFullName ),
            users!appointments_userId_fkey ( userAssignedCalendarName )
          `)
          .eq("appointmentId", preferredAppointmentId)
          .eq("patientId", patientId)
          .maybeSingle();

        if (preferredAppointmentError) {
          console.error("Erro ao buscar agendamento preferencial para vínculo:", preferredAppointmentError);
        } else if (preferredAppointmentRow) {
          const startDateTime = new Date(preferredAppointmentRow.appointmentStartDate);
          const endDateTime = preferredAppointmentRow.appointmentEndDate
            ? new Date(preferredAppointmentRow.appointmentEndDate)
            : startDateTime;

          const preferredAppointment: AgendaEventIntegrated = {
            id: preferredAppointmentRow.appointmentId,
            title: preferredAppointmentRow.appointmentTitle || "(Sem Título)",
            subject: preferredAppointmentRow.appointmentTitle,
            start: startDateTime,
            end: endDateTime,
            userId: preferredAppointmentRow.userId || undefined,
            doctorId: preferredAppointmentRow.doctorId || undefined,
            patientId: preferredAppointmentRow.patientId || undefined,
            patientName: preferredAppointmentRow.patients?.patientFullName || undefined,
            status: preferredAppointmentRow.appointmentStatus || "scheduled",
            location: preferredAppointmentRow.appointmentLocation || undefined,
            calendar_name: preferredAppointmentRow.users?.userAssignedCalendarName || "(Não Atribuído)",
            appointmentStatus: {
              hasEvolution: !!preferredAppointmentRow.appointmentEvolutionId,
              evolutionId: preferredAppointmentRow.appointmentEvolutionId || undefined,
              isBilled: preferredAppointmentRow.appointmentIsBilled || false,
              invoiceId: preferredAppointmentRow.invoiceId || undefined,
              paymentOrigin: preferredAppointmentRow.appointmentPaymentOrigin || undefined,
            },
            absenceInfo: preferredAppointmentRow.appointmentAbsenceInfo || undefined
          };

          if (preferredAppointment.status === 'scheduled' || preferredAppointment.status === 'no_evaluated') {
            available.unshift(preferredAppointment);
          } else {
            currentLink = preferredAppointment;
          }
        }
      }

      console.log(`📊 Agendamentos categorizados:`);
      console.log(`   ✅ Disponíveis para vínculo: ${available.length}`);
      console.log(`   🧾 Sessões avaliadas (exibindo ${limitedLinked.length} de ${linked.length}): ${linked.length}`);
      console.log(`   📌 Vínculo atual: ${currentLink ? 'Sim' : 'Não'}`);

      return {
        available,
        linked: limitedLinked,
        currentLink
      };
    } catch (error) {
      console.error('❌ Erro ao categorizar agendamentos:', error);
      return {
        available: [],
        linked: [],
        currentLink: null
      };
    }
  }

  /**
   * 🆕 Desvincular evolução de um agendamento
   * @param evolutionId ID da evolução
   * @param appointmentId ID do agendamento
   */
  static async unlinkEvolutionFromAppointment(
    evolutionId: string,
    appointmentId: string
  ): Promise<void> {
    try {
      await this.assertAppointmentEditable(appointmentId);
      const currentAttachments = await this.loadMedicalRecordAttachments(evolutionId);
      const nowIso = new Date().toISOString();
      const nextAttachments = {
        ...currentAttachments,
        unlinkedAt: nowIso,
        appointmentUnlinkInfo: {
          appointmentId,
          source: 'manual_unlink',
          timestamp: nowIso,
        },
      };

      delete nextAttachments.agendamentoId;

      const { error: evolutionError } = await supabase
        .from("medical_records")
        .update({
          recordAttachments: nextAttachments,
          recordUpdatedAt: nowIso,
        })
        .eq("recordId", evolutionId);
      
      if (evolutionError) {
        throw evolutionError;
      }

      // Voltar agendamento para status "no_evaluated"
      const { data: appointment, error: appointmentFetchError } = await supabase
        .from("appointments")
        .select("appointmentPaymentOrigin")
        .eq("appointmentId", appointmentId)
        .single();

      if (appointmentFetchError) {
        throw appointmentFetchError;
      }

      const { error: appointmentError } = await supabase
        .from("appointments")
        .update({
          appointmentStatus: 'no_evaluated',
          appointmentEvolutionId: null,
          appointmentPaymentOrigin: resolveAppointmentPaymentOriginPatch({
            currentOrigin: appointment?.appointmentPaymentOrigin,
            status: 'no_evaluated',
            hasNewFinancialImpact: false,
          }) ?? appointment?.appointmentPaymentOrigin ?? 'avulsa',
          appointmentUpdatedAt: nowIso
        })
        .eq("appointmentId", appointmentId);

      if (appointmentError) {
        throw appointmentError;
      }

      console.log(`✅ Evolução ${evolutionId} desvinculada do agendamento ${appointmentId}`);
    } catch (error) {
      console.error('Erro ao desvincular evolução:', error);
      throw error;
    }
  }

  static async relinkEvolution(
    evolutionId: string,
    oldAppointmentId: string | null,
    newAppointmentId: string
  ): Promise<RelinkEvolutionResult> {
    try {
      await this.assertAppointmentEditable(newAppointmentId);
      const { data: newAppointment, error: newAppointmentFetchError } = await supabase
        .from("appointments")
        .select("appointmentStatus")
        .eq("appointmentId", newAppointmentId)
        .single();

      if (newAppointmentFetchError) {
        throw newAppointmentFetchError;
      }

      const previousAppointmentStatus = newAppointment?.appointmentStatus;
      const isAllowedOriginStatus =
        previousAppointmentStatus === 'scheduled' || previousAppointmentStatus === 'no_evaluated';

      if (!isAllowedOriginStatus) {
        throw new Error(
          "A troca de vínculo só é permitida quando o novo agendamento está com status Agendado ou Não avaliado. Ajuste o status na Agenda e tente novamente."
        );
      }

      // Se havia vínculo anterior, desvincular
      if (oldAppointmentId) {
        await this.assertAppointmentEditable(oldAppointmentId);
        const { data: oldAppointment, error: oldAppointmentFetchError } = await supabase
          .from("appointments")
          .select("appointmentPaymentOrigin")
          .eq("appointmentId", oldAppointmentId)
          .single();

        if (oldAppointmentFetchError) {
          throw oldAppointmentFetchError;
        }

        const { error: oldAppointmentError } = await supabase
          .from("appointments")
          .update({
            appointmentStatus: previousAppointmentStatus,
            appointmentEvolutionId: null,
            appointmentPaymentOrigin: resolveAppointmentPaymentOriginPatch({
              currentOrigin: oldAppointment?.appointmentPaymentOrigin,
              status: previousAppointmentStatus,
              hasNewFinancialImpact: false,
            }) ?? oldAppointment?.appointmentPaymentOrigin ?? 'avulsa',
            appointmentUpdatedAt: new Date().toISOString()
          })
          .eq("appointmentId", oldAppointmentId);

        if (oldAppointmentError) {
          throw oldAppointmentError;
        }
      }

      // Vincular evolução ao novo agendamento (medical_records)
      await this.assertAppointmentEditable(newAppointmentId);
      await this.linkEvolutionToAppointment(evolutionId, newAppointmentId);

      // Concluir novo agendamento sem rotina financeira adicional
      const { error: newAppointmentUpdateError } = await supabase
        .from("appointments")
        .update({
          appointmentStatus: 'completed',
          appointmentEvolutionId: evolutionId,
          appointmentCompletedAt: new Date().toISOString(),
          appointmentPaymentOrigin: resolveAppointmentPaymentOriginPatch({
            currentOrigin: null,
            status: 'completed',
            hasNewFinancialImpact: false,
          }) ?? 'avulsa',
          appointmentUpdatedAt: new Date().toISOString(),
        })
        .eq("appointmentId", newAppointmentId);

      if (newAppointmentUpdateError) {
        throw newAppointmentUpdateError;
      }

      console.log(`✅ Evolução ${evolutionId} revinculada ao agendamento ${newAppointmentId}`);
      return {
        previousAppointmentStatus,
      };
    } catch (error) {
      console.error('Erro ao revincular evolução:', error);
      throw error;
    }
  }
}
